import { useI18n } from '../../../i18n';
// ============================================
// TransactionStatsRow Component
// 4 KPI stat cards for the Transactions page
// ============================================

import { StatCard, StatBadge } from '../../../components/shared/StatCard';
import {
  DollarIcon,
  StoresIcon,
  TrendingUpIcon,
  AlertTriangleIcon,
} from '../../../components/icons';

// ============================================
// Types
// ============================================

interface TransactionStatsRowProps {
  /** Total transaction volume (e.g., "$2.4M") */
  totalVolume: string;
  /** Trend percentage for total volume (e.g., 12.5 for +12.5%) */
  totalVolumeTrend?: number;
  /** Number of active stores */
  activeStores: number;
  /** Active stores subtitle (e.g., "4 across 12 regions") */
  activeStoresSubtitle: string;
  /** Average transaction value (e.g., "$148.20") */
  avgTransaction: string;
  /** Avg transaction subtitle (e.g., "Stable daily trend") */
  avgTxnSubtitle: string;
  /** Failed payment rate (e.g., "0.42%") */
  failedRate: string;
  /** Failed rate trend text (e.g., "-0.08% from avg") */
  failedRateTrend?: string;
  /** Loading state */
  isLoading?: boolean;
}

// ============================================
// Component
// ============================================

export function TransactionStatsRow({
  totalVolume,
  totalVolumeTrend,
  activeStores,
  activeStoresSubtitle,
  avgTransaction,
  avgTxnSubtitle,
  failedRate,
  failedRateTrend,
  isLoading,
}: TransactionStatsRowProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[128px] animate-pulse rounded-xl bg-white shadow-sm"
          />
        ))}
      </div>
    );
  }

  const trendIsPositive = totalVolumeTrend && totalVolumeTrend > 0;

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Total Volume */}
      <StatCard
        variant="large"
        icon={<DollarIcon size={24} className="text-emerald-600" />}
        iconBg="bg-emerald-50"
        label={t('totalVolume')}
        value={totalVolume}
        badge={
          totalVolumeTrend !== undefined ? (
            <StatBadge variant={trendIsPositive ? 'success' : 'danger'}>
              {trendIsPositive ? '+' : ''}
              {totalVolumeTrend}%
            </StatBadge>
          ) : undefined
        }
      />

      {/* Active Stores */}
      <StatCard
        variant="large"
        icon={<StoresIcon size={24} className="text-blue-600" />}
        iconBg="bg-blue-50"
        label={t('activeStores')}
        value={activeStores}
        badge={
          <span className="text-xs text-slate-500">{activeStoresSubtitle}</span>
        }
      />

      {/* Avg. Transaction */}
      <StatCard
        variant="large"
        icon={<TrendingUpIcon size={24} className="text-primary-600" />}
        iconBg="bg-blue-50"
        label={t('avgTransaction')}
        value={avgTransaction}
        badge={
          <span className="text-xs text-slate-500">{avgTxnSubtitle}</span>
        }
      />

      {/* Failed Payments */}
      <StatCard
        variant="large"
        icon={<AlertTriangleIcon size={24} className="text-red-500" />}
        iconBg="bg-red-50"
        label={t('failedPayments')}
        value={failedRate}
        valueColor="text-red-600"
        badge={
          failedRateTrend ? (
            <span className="text-xs text-red-500">{failedRateTrend}</span>
          ) : undefined
        }
      />
    </div>
  );
}
